import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { GenerationProgress } from 'types'
import { type AlmanacCableConsumer, type AlmanacCableSubscription } from 'providers/CableConsumer/types'
import { isFileFromLatestBatch } from 'screens/ScheduledPosts/utils'
import {
  handlePreviewChannelMessage,
  isPreviewSyncAssetType,
  PreviewChannelContentUpdate,
  PreviewChannelMessage,
  PreviewChannelPreviewUpdate,
} from './previewChannelUpdates'

export interface DesignPreviewUpdate extends PreviewChannelPreviewUpdate {
  is_generating: boolean
  session_uuid: string
}

export interface DesignContentUpdate extends PreviewChannelContentUpdate {}

export interface DesignProgressUpdate {
  op: 'progress_update'
  progress: GenerationProgress
  reference_image_url?: string
}

export const useDesignPreviewSync = (eventsData: AlmanacFileCalendarItem[]) => {
  const auth = useAuth()
  const websocketConnection = useWebsocketConnection()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const subscriptionsRef = useRef<Map<string, AlmanacCableSubscription>>(new Map())
  const activeConnectionRef = useRef<AlmanacCableConsumer | null>(null)

  const serializedDesignKeys = useMemo(() => {
    const designKeys = new Set<string>()
    eventsData.forEach((event) => {
      if (!isPreviewSyncAssetType(event?.asset_type) || !event?.key) return
      const needsPreview = event.image_url_generating || event.is_generating || !event.image_url
      if (needsPreview || (event.created_at && isFileFromLatestBatch(event))) {
        designKeys.add(event.key)
      }
    })

    return JSON.stringify(Array.from(designKeys).sort())
  }, [eventsData])

  const unsubscribeAll = useCallback(() => {
    subscriptionsRef.current.forEach((subscription) => {
      subscription.unsubscribe()
    })
    subscriptionsRef.current.clear()
    activeConnectionRef.current = null
  }, [])

  useEffect(() => {
    return () => {
      unsubscribeAll()
    }
  }, [unsubscribeAll])

  useEffect(() => {
    if (!auth || !websocketConnection) {
      unsubscribeAll()
      return
    }

    if (activeConnectionRef.current && activeConnectionRef.current !== websocketConnection) {
      unsubscribeAll()
    }

    activeConnectionRef.current = websocketConnection

    const designKeys = JSON.parse(serializedDesignKeys) as string[]
    const activeDesignKeys = new Set(designKeys)
    const subscriptions = subscriptionsRef.current

    subscriptions.forEach((subscription, key) => {
      if (activeDesignKeys.has(key)) return

      subscription.unsubscribe()
      subscriptions.delete(key)
    })

    designKeys.forEach((key) => {
      if (subscriptions.has(key)) return

      const subscription = websocketConnection.subscriptions.create(
        {
          channel: 'DesignPreviewChannel',
          key,
        },
        {
          received(data: DesignPreviewUpdate | DesignContentUpdate | DesignProgressUpdate | PreviewChannelMessage) {
            if (data.op === 'progress_update') {
              optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, key, (f) => ({
                ...f,
                progress: data.progress,
                ...(data.reference_image_url ? { reference_image_url: data.reference_image_url } : {}),
              }))

              updateFileDetailCache(queryClient, workspaceId, key, (file) => ({
                ...file,
                progress: data.progress,
                ...(data.reference_image_url ? { reference_image_url: data.reference_image_url } : {}),
              }))
              return
            }

            handlePreviewChannelMessage(queryClient, workspaceId, key, data)
          },
        },
      )

      subscriptions.set(key, subscription)
    })
  }, [auth, websocketConnection, serializedDesignKeys, queryClient, workspaceId, unsubscribeAll])
}
